import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Select, MenuItem, FormControl, InputLabel, Snackbar, Alert,
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import { ArticleStatusChip } from '../../article/components/ArticleStatusChip'
import { dashboardApi } from '../services/dashboardApi'
import type { DashboardArticle } from '../types/dashboard.types'
import type { ApiError, SnackbarState } from '../../../shared/types/common'

export function DashboardPage() {
  const navigate = useNavigate()
  const [articles, setArticles] = useState<DashboardArticle[]>([])
  const [stateFilter, setStateFilter] = useState('')
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' })
  const [loading, setLoading] = useState(true)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; article: DashboardArticle | null }>({ open: false, article: null })

  useEffect(() => { fetchArticles(); }, [stateFilter]);

  async function fetchArticles() {
    setLoading(true);
    try {
      const params = stateFilter ? { state: stateFilter } : {};
      const res = await dashboardApi.listArticles(params);
      setArticles(res.data);
    } catch {
      showSnackbar('Failed to load articles', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(id: number) {
    try {
      await dashboardApi.submitArticle(id);
      showSnackbar('Article submitted for review');
      fetchArticles();
    } catch (err) {
      showSnackbar((err as ApiError).response?.data?.message || 'Failed to submit', 'error')
    }
  }

  async function handleDeleteDraft() {
    if (!deleteDialog.article) {
      return
    }

    try {
      await dashboardApi.deleteArticle(deleteDialog.article.id)
      showSnackbar('Draft technote deleted')
      setDeleteDialog({ open: false, article: null })
      await fetchArticles()
    } catch (err) {
      showSnackbar((err as ApiError).response?.data?.message || 'Failed to delete draft', 'error')
    }
  }

  function showSnackbar(message: string, severity: SnackbarState['severity'] = 'success') {
    setSnackbar({ open: true, message, severity })
  }

  return (
    <Box>
      {/* Page header card — matches mockup topbar pattern */}
      <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid #0f4c81' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5">My Technotes</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Create and manage your technical notes through the review and approval workflow.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/articles/new')}>
            New Technote
          </Button>
        </Box>
      </Paper>

      <Box mb={2}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filter by State</InputLabel>
          <Select value={stateFilter} label="Filter by State" onChange={e => setStateFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="review">In Review</MenuItem>
            <MenuItem value="changes_requested">Changes Requested</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="published">Published</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Discipline</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Loading...</TableCell></TableRow>
            ) : articles.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No technotes found</TableCell></TableRow>
            ) : articles.map(article => (
              <TableRow key={article.id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{article.technote_id || `#${article.id}`}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{article.title}</TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{article.discipline || '—'}</TableCell>
                <TableCell>{article.customer || '—'}</TableCell>
                <TableCell><ArticleStatusChip state={article.state} /></TableCell>
                <TableCell>{new Date(article.updated_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Tooltip title="View">
                    <IconButton size="small" onClick={() => navigate(`/articles/${article.id}`)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {['draft', 'changes_requested'].includes(article.state) && (
                    <>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => navigate(`/articles/${article.id}/edit`)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {['draft', 'changes_requested'].includes(article.state) && (
                    <>
                      <Tooltip title="Submit for Review">
                        <IconButton size="small" sx={{ color: '#0f4c81' }} onClick={() => handleSubmit(article.id)}>
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {article.state === 'draft' && (
                    <Tooltip title="Delete draft">
                      <IconButton
                        size="small"
                        sx={{ color: '#b42318' }}
                        onClick={() => setDeleteDialog({ open: true, article })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, article: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete draft technote?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This permanently deletes <strong>{deleteDialog.article?.technote_id || deleteDialog.article?.title}</strong> from the portal.
            Draft technotes are not published to Wiki.js, and this action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, article: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void handleDeleteDraft()}>
            Delete Draft
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
